import React, { useState } from "react";
import { useSelector } from "react-redux";
import TableLine from "./TableLine";
import CoinCard from "./CoinCard";
import ToTop from "./ToTop";
import { isStableCoin } from "./Utils";
import { ArrowUpDown, ArrowUp, ArrowDown, SearchX } from "lucide-react";

const Table = ({ coinsData, searchTerm, viewMode, onOpenModal }) => {
  const [orderBy, setOrderBy] = useState("");
  const [rangeNumber, setRangeNumber] = useState(100);
  const showStable = useSelector((state) => state.stableReducer.showStable);
  const showFavList = useSelector((state) => state.listReducer.showList);

  const tableHeader = [
    { label: "Price", key: "Prix" },
    { label: "Market Cap", key: "MarketCap" },
    { label: "Volume", key: "Volume" },
    { label: "1h", key: "1h" },
    { label: "1d", key: "1j" },
    { label: "1w", key: "1s" },
    { label: "1m", key: "1m" },
    { label: "6m", key: "6m" },
    { label: "1y", key: "1y" },
    { label: "ATH", key: "ATH" },
  ];

  const handleHeaderClick = (key) => {
    if (orderBy === key) {
      setOrderBy(key + "reverse");
    } else {
      setOrderBy(key);
    }
  };

  const getSortIcon = (key) => {
    if (orderBy === key) return <ArrowDown size={12} className="sort-icon active" />;
    if (orderBy === key + "reverse") return <ArrowUp size={12} className="sort-icon active" />;
    return <ArrowUpDown size={12} className="sort-icon muted" />;
  };

  // 1. Filter Stablecoins
  const filteredStable = (coinsData || []).filter((coin) => {
    if (showStable) return true;
    return isStableCoin(coin.symbol);
  });

  // 2. Filter Favorites
  const filteredFav = filteredStable.filter((coin) => {
    if (showFavList) {
      if (!window.localStorage.coinList) return false;
      const list = window.localStorage.coinList.split(",");
      return list.includes(coin.id);
    }
    return true;
  });

  // 3. Filter Search Term
  const filteredSearch = filteredFav.filter((coin) => {
    if (!searchTerm || searchTerm.trim() === "") return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      coin.name.toLowerCase().includes(term) ||
      coin.symbol.toLowerCase().includes(term)
    );
  });

  // 4. Sort Coins
  const sortedCoins = [...filteredSearch].sort((a, b) => {
    switch (orderBy) {
      case "Prix":
        return b.current_price - a.current_price;
      case "Prixreverse":
        return a.current_price - b.current_price;
      case "Volume":
        return b.total_volume - a.total_volume;
      case "Volumereverse":
        return a.total_volume - b.total_volume;
      case "MarketCap":
        return b.market_cap - a.market_cap;
      case "MarketCapreverse":
        return a.market_cap - b.market_cap;
      case "1h":
        return (b.price_change_percentage_1h_in_currency || 0) - (a.price_change_percentage_1h_in_currency || 0);
      case "1hreverse":
        return (a.price_change_percentage_1h_in_currency || 0) - (b.price_change_percentage_1h_in_currency || 0);
      case "1j":
        return (b.market_cap_change_percentage_24h || 0) - (a.market_cap_change_percentage_24h || 0);
      case "1jreverse":
        return (a.market_cap_change_percentage_24h || 0) - (b.market_cap_change_percentage_24h || 0);
      case "1s":
        return (b.price_change_percentage_7d_in_currency || 0) - (a.price_change_percentage_7d_in_currency || 0);
      case "1sreverse":
        return (a.price_change_percentage_7d_in_currency || 0) - (b.price_change_percentage_7d_in_currency || 0);
      case "1m":
        return (b.price_change_percentage_30d_in_currency || 0) - (a.price_change_percentage_30d_in_currency || 0);
      case "1mreverse":
        return (a.price_change_percentage_30d_in_currency || 0) - (b.price_change_percentage_30d_in_currency || 0);
      case "6m":
        return (b.price_change_percentage_200d_in_currency || 0) - (a.price_change_percentage_200d_in_currency || 0);
      case "6mreverse":
        return (a.price_change_percentage_200d_in_currency || 0) - (b.price_change_percentage_200d_in_currency || 0);
      case "1y":
        return (b.price_change_percentage_1y_in_currency || 0) - (a.price_change_percentage_1y_in_currency || 0);
      case "1yreverse":
        return (a.price_change_percentage_1y_in_currency || 0) - (b.price_change_percentage_1y_in_currency || 0);
      case "ATH":
        return (b.ath_change_percentage || 0) - (a.ath_change_percentage || 0);
      case "ATHreverse":
        return (a.ath_change_percentage || 0) - (b.ath_change_percentage || 0);
      default:
        return 0;
    }
  });

  const displayedCoins = sortedCoins.slice(0, rangeNumber);

  return (
    <div className="table-container">
      {/* Controls Header & Top N range slider */}
      <div className="table-range-header">
        <div className="range-controls">
          <span className="range-label">
            Displaying Top: <strong className="range-value">{rangeNumber}</strong> cryptos
          </span>
          <input
            type="range"
            min="5"
            max="250"
            step="5"
            value={rangeNumber}
            onChange={(e) => setRangeNumber(Number(e.target.value))}
            className="modern-range-slider"
          />
        </div>

        <div className="results-count">
          {displayedCoins.length} results displayed
        </div>
      </div>

      {displayedCoins.length === 0 ? (
        <div className="empty-results">
          <SearchX size={48} className="empty-icon" />
          <h3>No cryptocurrencies found</h3>
          <p>Try adjusting your search or filter criteria.</p>
        </div>
      ) : viewMode === "cards" ? (
        <div className="cards-grid">
          {displayedCoins.map((coin, index) => (
            <CoinCard
              key={coin.id}
              coin={coin}
              index={index}
              onOpenModal={onOpenModal}
            />
          ))}
        </div>
      ) : (
        <>
          <ul className="table-header">
            <li className="header-info-col"># Coin</li>
            {tableHeader.map((el) => (
              <li
                key={el.key}
                onClick={() => handleHeaderClick(el.key)}
                className={`sortable-col ${
                  orderBy === el.key || orderBy === el.key + "reverse" ? "active" : ""
                }`}
              >
                <span>{el.label}</span>
                {getSortIcon(el.key)}
              </li>
            ))}
          </ul>

          <div className="table-lines-list">
            {displayedCoins.map((coin, index) => (
              <TableLine
                coin={coin}
                key={coin.id}
                index={index}
                onOpenModal={onOpenModal}
              />
            ))}
          </div>
        </>
      )}

      <ToTop />
    </div>
  );
};

export default Table;
